import React from 'react';
import {
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Button,
  makeStyles,
} from '@fluentui/react-components';
import { ArrowDownload24Regular, Document24Regular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  button: {
    minWidth: '120px',
  },
});

interface ExportColumn {
  key: string;
  label: string;
}

interface ExportButtonProps {
  data: any[];
  filename: string;
  columns: ExportColumn[];
  disabled?: boolean;
}

export const ExportButton: React.FC<ExportButtonProps> = ({
  data,
  filename,
  columns,
  disabled = false,
}) => {
  const styles = useStyles();

  const convertToCSV = (data: any[], columns: ExportColumn[]): string => {
    if (data.length === 0) return '';

    // Header
    const header = columns.map((col) => `"${col.label}"`).join(',');

    // Rows
    const rows = data.map((item) => {
      return columns
        .map((col) => {
          const value = item[col.key];
          // Handle null/undefined
          if (value === null || value === undefined) return '""';
          // Handle objects (convert to JSON string)
          if (typeof value === 'object') return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
          // Handle strings (escape quotes)
          return `"${String(value).replace(/"/g, '""')}"`;
        })
        .join(',');
    });

    return [header, ...rows].join('\n');
  };

  const downloadCSV = (csvContent: string, filename: string) => {
    const BOM = '\uFEFF'; // UTF-8 BOM for Excel compatibility
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const fullFilename = `${filename}_${timestamp}.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', fullFilename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleExportCSV = () => {
    const csvContent = convertToCSV(data, columns);
    downloadCSV(csvContent, filename);
  };

  return (
    <Menu>
      <MenuTrigger disableButtonEnhancement>
        <Button
          className={styles.button}
          appearance="primary"
          icon={<ArrowDownload24Regular />}
          disabled={disabled || data.length === 0}
        >
          Exportar
        </Button>
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          <MenuItem icon={<Document24Regular />} onClick={handleExportCSV}>
            Exportar CSV ({data.length} {data.length === 1 ? 'item' : 'itens'})
          </MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

export default ExportButton;
